import { Button, Card, Typography, message, Modal, Input } from "antd";
import React, { useState } from "react";
import { useAuth } from "~/store";
import {
  useGetStaffActivityToday,
  useGetStaffWeeklyActivities,
  useGetStaffCurrentStatusActivity,
  OPERATION_KEYS,
  operationServices,
} from "~/services/operation";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { TWorkSession } from "~/types";

const { Title, Text } = Typography;

export const TimeTracking: React.FC = () => {
  const { user } = useAuth();
  const staff = user?.staff;
  const staffId = staff?.id;

  const queryClient = useQueryClient();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activityInput, setActivityInput] = useState("");

  const { data: currentStatus, isLoading: isLoadingStatus } =
    useGetStaffCurrentStatusActivity(staffId);

  const { data: todayActivities, isLoading: isLoadingToday } =
    useGetStaffActivityToday(staffId);

  const [viewMode, setViewMode] = useState<"day" | "week">("day");
  const {
    data: weeklyActivities,
    isLoading: isLoadingWeekly,
    refetch: fetchWeeklyActivities,
  } = useGetStaffWeeklyActivities(staffId, undefined, {
    enabled: false,
  });

  const clockInMutation = useMutation({
    mutationFn: (body: { id: string; activity?: string }) =>
      operationServices.clockIn(body),
    onSuccess: () => {
      message.success("Clocked in successfully");
      queryClient.invalidateQueries({
        queryKey: OPERATION_KEYS.GET_STAFF_CURRENT_STATUS_ACTIVITY(staffId!),
      });
      queryClient.invalidateQueries({
        queryKey: OPERATION_KEYS.GET_STAFF_ACTIVITY_TODAY(staffId!),
      });
    },
    onError: () => {
      message.error("Failed to clock in");
    },
  });

  const clockOutMutation = useMutation({
    mutationFn: (id: string) => operationServices.clockOut(id),
    onSuccess: () => {
      message.success("Clocked out successfully");
      queryClient.invalidateQueries({
        queryKey: OPERATION_KEYS.GET_STAFF_CURRENT_STATUS_ACTIVITY(staffId!),
      });
      queryClient.invalidateQueries({
        queryKey: OPERATION_KEYS.GET_STAFF_ACTIVITY_TODAY(staffId!),
      });
    },
    onError: () => {
      message.error("Failed to clock out");
    },
  });

  const formatTime = (date: string) => {
    return new Date(date).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatTimeRange = (clockIn: string, clockOut: string | null) => {
    const start = formatTime(clockIn);
    const end = clockOut ? formatTime(clockOut) : "Current";
    return `${start} - ${end}`;
  };

  const showClockInModal = () => {
    if (!staffId) return;
    setActivityInput("");
    setIsModalOpen(true);
  };

  const handleClockIn = () => {
    if (!staffId) return;
    if (!activityInput.trim()) {
      message.error("Please enter an activity");
      return;
    }
    clockInMutation.mutate({ id: staffId, activity: activityInput });
    setIsModalOpen(false);
  };

  const handleClockOut = () => {
    if (!staffId) return;
    clockOutMutation.mutate(staffId);
  };

  const handleViewDay = () => {
    setViewMode("day");
  };

  const handleViewWeek = () => {
    fetchWeeklyActivities();
    setViewMode("week");
  };

  const groupActivitiesByDay = (activities: TWorkSession[]) => {
    const grouped: { [key: string]: TWorkSession[] } = {};
    activities.forEach((activity) => {
      const day = new Date(activity.clockIn).toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
      if (!grouped[day]) {
        grouped[day] = [];
      }
      grouped[day].push(activity);
    });
    return grouped;
  };

  const activitiesToDisplay: TWorkSession[] =
    viewMode === "day"
      ? todayActivities?.activities || []
      : weeklyActivities?.activities || [];
  const totalHoursToDisplay =
    viewMode === "day"
      ? todayActivities?.totalHours || 0
      : weeklyActivities?.totalHours || 0;
  const groupedActivities =
    viewMode === "week" ? groupActivitiesByDay(activitiesToDisplay) : null;

  const isLoading = isLoadingStatus || isLoadingToday || isLoadingWeekly;

  return (
    <div className="space-y-3 md:space-y-4 md:space-y-4">
      <Title level={3} className="mb-0 text-lg md:text-xl ">
        Time Tracking
      </Title>
      <Card
        classNames={{
          body: "p-0",
        }}
        className="mb-3 md:mb-4 xl:mb-6 border-0 md:border"
      >
        <div className="p-3 md:p-4 xl:p-5 border-b">
          <Title level={4} className="mb-0 text-base md:text-lg xl:text-xl">
            Current Status
          </Title>
        </div>
        <div className="p-4 md:p-6 xl:p-8 text-center">
          {isLoading ? (
            <Text className="text-sm md:text-base">Loading...</Text>
          ) : (
            <>
              <Title level={2} className="mb-2 text-xl md:text-2xl xl:text-3xl">
                {currentStatus?.isClockedIn ? "Clocked In" : "Clocked Out"}
              </Title>
              {currentStatus?.isClockedIn && currentStatus?.lastClockIn && (
                <Text type="secondary" className="mb-3 md:mb-4 block text-sm md:text-base">
                  Since {formatTime(currentStatus.lastClockIn)}
                </Text>
              )}
              <Button
                type="primary"
                danger={currentStatus?.isClockedIn}
                onClick={
                  currentStatus?.isClockedIn ? handleClockOut : showClockInModal
                }
                loading={
                  clockInMutation.isPending || clockOutMutation.isPending
                }
                size="large"
                className="w-full md:w-auto"
              >
                {currentStatus?.isClockedIn ? "Clock Out" : "Clock In"}
              </Button>
            </>
          )}
        </div>
      </Card>
      <Card
        classNames={{
          body: "p-0",
        }}
        className="border-0 md:border"
      >
        <div className="p-3 md:p-4 xl:p-5 border-b">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 md:gap-4">
            <Title level={4} className="mb-0 text-base md:text-lg xl:text-xl">
              {viewMode === "day" ? "Today's Activities" : "Weekly Activities"}
            </Title>
            <Button
              type="link"
              onClick={viewMode === "day" ? handleViewWeek : handleViewDay}
              size="small"
            >
              {viewMode === "day" ? "View Week" : "View Day"}
            </Button>
          </div>
        </div>
        <div className="p-3 md:p-4 xl:p-5">
          <div className="space-y-3 md:space-y-4">
            {isLoading ? (
              <Text className="text-sm md:text-base">Loading...</Text>
            ) : viewMode === "day" ? (
              activitiesToDisplay.length > 0 ? (
                activitiesToDisplay.map((activity, index) => (
                  <div key={index} className="border-b pb-3 md:pb-4 last:border-b-0 last:pb-0">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-2 md:gap-4">
                      <div className="flex-1 min-w-0">
                        <Text strong className="text-sm md:text-base block">
                          {activity.activity}
                        </Text>
                        <Text type="secondary" className="text-xs md:text-sm block">
                          {formatTimeRange(activity.clockIn, activity.clockOut)}
                        </Text>
                      </div>
                      <Text className="text-xs md:text-sm font-medium flex-shrink-0">
                        {activity.duration
                          ? `${activity.duration.toFixed(2)} hours`
                          : "Ongoing"}
                      </Text>
                    </div>
                  </div>
                ))
              ) : (
                <Text className="text-sm md:text-base text-center py-4 md:py-6">
                  No activities for today
                </Text>
              )
            ) : groupedActivities &&
              Object.keys(groupedActivities).length > 0 ? (
              Object.keys(groupedActivities).map((day) => (
                <div key={day} className="border-b pb-3 md:pb-4 last:border-b-0 last:pb-0">
                  <Text strong className="block mb-2 md:mb-3 text-sm md:text-base">
                    {day}
                  </Text>
                  {groupedActivities[day].map((activity, index) => (
                    <div
                      key={index}
                      className="flex flex-col md:flex-row justify-between items-start md:items-center mb-2 gap-1 md:gap-4"
                    >
                      <div className="flex-1 min-w-0">
                        <Text className="text-sm md:text-base block">
                          {activity.activity}
                        </Text>
                        <Text type="secondary" className="text-xs md:text-sm block">
                          {formatTimeRange(activity.clockIn, activity.clockOut)}
                        </Text>
                      </div>
                      <Text className="text-xs md:text-sm font-medium flex-shrink-0">
                        {activity.duration
                          ? `${activity.duration.toFixed(2)} hours`
                          : "Ongoing"}
                      </Text>
                    </div>
                  ))}
                </div>
              ))
            ) : (
              <Text className="text-sm md:text-base text-center py-4 md:py-6">
                No activities for this week
              </Text>
            )}
          </div>
          <div className="mt-4 md:mt-6 pt-3 md:pt-4 border-t flex flex-col md:flex-row justify-between items-start md:items-center gap-2 md:gap-4">
            <Text strong className="text-sm md:text-base">
              {viewMode === "day" ? "Total Today" : "Total Week"}
            </Text>
            <Text strong className="text-sm md:text-base">
              {totalHoursToDisplay.toFixed(2)} hours
            </Text>
          </div>
        </div>
      </Card>

      <Modal
        title="Clock In"
        open={isModalOpen}
        onOk={handleClockIn}
        onCancel={() => setIsModalOpen(false)}
        okText="Clock In"
        cancelText="Cancel"
        okButtonProps={{ loading: clockInMutation.isPending }}
        width={window.innerWidth < 768 ? "95%" : 500}
      >
        <div className="mb-3 md:mb-4">
          <Text className="text-sm md:text-base">
            Please enter the activity for this session:
          </Text>
        </div>
        <Input
          placeholder="Enter activity (e.g., Morning Prep)"
          value={activityInput}
          onChange={(e) => setActivityInput(e.target.value)}
          size="large"
        />
      </Modal>
    </div>
  );
};
